import { db } from "@/_db";
import { collection, addDoc, onSnapshot } from "firebase/firestore";

interface FootprintsResponse {
  footprint_id: string;
  consumption_ids: string[];
  product_ids: string[];
}

interface LoadFootprintsResponse {
  exists: boolean;
  formData: Record<string, any> | null;
  status: "draft" | "submitted" | null;
  lastUpdated: Date | null;
}

interface StatusResponse {
  exists: boolean;
  status: "draft" | "submitted" | null;
  lastUpdated: Date | null;
}

// Cache to prevent duplicate requests
const activeRequests = new Map<string, Promise<LoadFootprintsResponse>>();
const activeUnsubscribers = new Map<string, () => void>();

// Constants
const REQUEST_TIMEOUT = 60000; // 60 seconds
const REQUEST_COLLECTION = "request";

/**
 * Recursively remove undefined values from an object
 * This is necessary because Firestore doesn't accept undefined values
 */
const removeUndefinedValues = (obj: any): any => {
  if (obj === null || obj === undefined) {
    return obj;
  }

  if (Array.isArray(obj)) {
    return obj.map(removeUndefinedValues);
  }

  if (typeof obj === "object") {
    const cleaned: any = {};
    for (const [key, value] of Object.entries(obj)) {
      if (value !== undefined) {
        cleaned[key] = removeUndefinedValues(value);
      }
    }
    return cleaned;
  }

  return obj;
};

/**
 * Save footprints data as draft
 */
export const saveFootprintsAsDraft = async (
  year: string,
  company_id: string,
  formData: Record<string, any>
): Promise<FootprintsResponse> => {
  const dataWithDraftStatus = {
    ...formData,
    _metadata: {
      ...formData._metadata,
      status: "draft",
      savedManually: true,
    },
  };

  return makeFootprintsRequest(year, company_id, dataWithDraftStatus);
};

/**
 * Save footprints data as final submission
 */
export const saveFootprintsAsFinal = async (
  year: string,
  company_id: string,
  formData: Record<string, any>
): Promise<FootprintsResponse> => {
  const dataWithSubmittedStatus = {
    ...formData,
    _metadata: {
      ...formData._metadata,
      status: "submitted",
      savedManually: true,
    },
  };

  return makeFootprintsRequest(year, company_id, dataWithSubmittedStatus);
};

/**
 * Load footprints data for a specific year and company
 */
export const loadFootprintsData = async (year: string, company_id: string): Promise<LoadFootprintsResponse> => {
  const requestKey = `${year}-${company_id}`;

  // Check if there's already an active request for this key
  if (activeRequests.has(requestKey)) {
    return activeRequests.get(requestKey)!;
  }

  // Clean up any previous unsubscriber for this key
  cleanupExistingRequest(requestKey);

  const requestPromise = createLoadRequest(year, company_id, requestKey);

  // Store the promise to prevent duplicate requests
  activeRequests.set(requestKey, requestPromise);

  try {
    return await requestPromise;
  } catch (error) {
    console.error("Error loading footprints data:", error);
    throw new Error("Failed to load footprints data");
  } finally {
    // Clean up after completion (success or failure)
    cleanupRequest(requestKey);
  }
};

/**
 * Get footprints data status (lightweight check)
 */
export const getFootprintsDataStatus = async (year: string, company_id: string): Promise<StatusResponse> => {
  try {
    const loadResponse = await loadFootprintsData(year, company_id);

    return {
      exists: loadResponse.exists,
      status: loadResponse.status,
      lastUpdated: loadResponse.lastUpdated,
    };
  } catch (error) {
    console.error("Error getting footprints data status:", error);
    return {
      exists: false,
      status: null,
      lastUpdated: null,
    };
  }
};

/**
 * Create a load request and handle the response
 */
const createLoadRequest = (year: string, company_id: string, requestKey: string): Promise<LoadFootprintsResponse> => {
  return new Promise<LoadFootprintsResponse>((resolve, reject) => {
    const requestData = {
      action: "load-footprints",
      data: {
        year,
        company_id,
      },
    };

    addDoc(collection(db, REQUEST_COLLECTION), requestData)
      .then((requestRef) => {
        const timeoutId = setTimeout(() => {
          console.error("Request timeout after 60 seconds for request:", requestRef.id);
          cleanupRequest(requestKey);
          reject(new Error("Request timeout"));
        }, REQUEST_TIMEOUT);

        const unsubscribe = onSnapshot(
          requestRef,
          (docSnapshot) => {
            if (docSnapshot.exists()) {
              const requestData = docSnapshot.data();

              // Check if the request has been processed (has status and result)
              if (requestData?.status) {
                clearTimeout(timeoutId);
                cleanupRequest(requestKey);

                if (requestData.status === "failure" || requestData.error) {
                  reject(new Error(requestData.error?.message || "Request failed"));
                } else if (requestData.status === "accepted" && requestData.entity !== undefined) {
                  resolve(requestData.entity as LoadFootprintsResponse);
                }
              }
            }
          },
          (error) => {
            clearTimeout(timeoutId);
            cleanupRequest(requestKey);
            reject(error);
          }
        );

        // Store the unsubscriber for cleanup
        activeUnsubscribers.set(requestKey, unsubscribe);
      })
      .catch((error) => {
        console.error("Error creating request:", error);
        cleanupRequest(requestKey);
        reject(new Error("Failed to create load request"));
      });
  });
};

/**
 * Internal function to make footprints save requests
 */
const makeFootprintsRequest = async (
  year: string,
  company_id: string,
  formData: Record<string, any>
): Promise<FootprintsResponse> => {
  try {
    // Clean the form data to remove undefined values before sending to Firestore
    const cleanedFormData = removeUndefinedValues(formData);

    const requestData = {
      action: "save-footprints",
      data: {
        year,
        company_id,
        formData: cleanedFormData,
      },
    };

    const requestRef = await addDoc(collection(db, REQUEST_COLLECTION), requestData);

    return new Promise((resolve, reject) => {
      const timeoutId = setTimeout(() => {
        reject(new Error("Request timeout"));
      }, REQUEST_TIMEOUT);

      const unsubscribe = onSnapshot(
        requestRef,
        (docSnapshot) => {
          if (docSnapshot.exists()) {
            const requestData = docSnapshot.data();

            // Check if the request has been processed (has status and result)
            if (requestData?.status) {
              clearTimeout(timeoutId);
              unsubscribe();

              if (requestData.status === "failure" || requestData.error) {
                reject(new Error(requestData.error?.message || "Request failed"));
              } else if (requestData.status === "accepted" && requestData.entity !== undefined) {
                resolve(requestData.entity as FootprintsResponse);
              }
            }
          }
        },
        (error) => {
          clearTimeout(timeoutId);
          unsubscribe();
          reject(error);
        }
      );
    });
  } catch (error) {
    console.error("Error making footprints request:", error);
    throw new Error("Failed to save footprints data");
  }
};

/**
 * Clean up existing request for a given key
 */
const cleanupExistingRequest = (requestKey: string) => {
  const existingUnsubscriber = activeUnsubscribers.get(requestKey);
  if (existingUnsubscriber) {
    existingUnsubscriber();
    activeUnsubscribers.delete(requestKey);
  }
};

/**
 * Clean up active request tracking
 */
const cleanupRequest = (requestKey: string) => {
  cleanupExistingRequest(requestKey);
  activeRequests.delete(requestKey);
};
